/**
 *  @swagger
 *  definitions:
 *    RegattaGalleryParams:
 *      type: object
 *      properties:
 *        RegattaId:
 *          description: ID of the associated Regatta
 *          type: string
 *        Public:
 *          description: Public of the RegattaGallery
 *          type: boolean
 *        Name:
 *          description: Name of the RegattaGallery
 *          type: string
 *        Value:
 *          description: Value of the RegattaGallery
 *          type: string
 *
 *  @swagger
 *  definitions:
 *    RegattaGalleryCreateParams:
 *      allOf:
 *        - $ref: '#/definitions/RegattaGalleryParams'
 *        - type: object
 *          required:
 *            - RegattaId
 *            - Public
 *            - Name
 *            - Value
 *
 *  @swagger
 *  definitions:
 *    RegattaGalleryQueryParams:
 *      allOf:
 *        - type: object
 *          properties:
 *            id:
 *              description: ID of the RegattaGallery
 *              type: string
 *        - $ref: '#/definitions/RegattaGalleryParams'
 *
 *  @swagger
 *  definitions:
 *    RegattaGalleryUpdateParams:
 *      allOf:
 *        - $ref: '#/definitions/RegattaGalleryQueryParams'
 *        - type: object
 *          required:
 *            - id
 *
 *  @swagger
 *  definitions:
 *    RegattaGallery:
 *      allOf:
 *        - $ref: '#/definitions/RegattaGalleryCreateParams'
 *        - $ref: '#/definitions/RegattaGalleryUpdateParams'
 *        - $ref: '#/definitions/Timestamps'
 *
 *  @swagger
 *  components:
 *    schemas:
 *      RegattaGallery:
 *        allOf:
 *          - $ref: '#/definitions/RegattaGallery'
 *          - type: object
 *            properties:
 *              Regatta:
 *                description: Regatta of the RegattaGallery
 *                $ref: '#/components/schemas/Regatta'
 */

// Types
import type {
  $Model,
  $ModelClass,
  $ModelClasses,
} from '.';
import type {
  $Field,
} from '../types';

export type $RelationIds = {
  RegattaId: string;
};

export type $Params = {
  Name: string;
  Public: boolean;
  Value: string;
};

export type $CreateParams = $Params & $RelationIds;

export type $UpdateParams = {
  id: string;
} & Partial<$CreateParams>;

export type $UpsertParams = $CreateParams & {
  id?: string;
};

export type $QueryParams = {
  id?: null | string;
} & Partial<$CreateParams>;

export type $Associations = {
  Regatta?: $Model<'Regatta'>;
};

export type $ModelParams = $Associations & $CreateParams & {
  id: string;
};

type $Fields = $Params & {
  id: string;
};

const fields = (
  Sequelize,
): Record<keyof $Fields, $Field> => ({
  id: {
    allowNull: false,
    defaultValue: Sequelize.UUIDV4,
    primaryKey: true,
    type: Sequelize.UUID,
    unique: true,
  },
  Name: {
    allowNull: false,
    type: Sequelize.STRING,
    validate: {
      notEmpty: true,
    },
  },
  Public: {
    allowNull: false,
    defaultValue: false,
    type: Sequelize.BOOLEAN,
  },
  Value: {
    allowNull: false,
    type: Sequelize.STRING,
    validate: {
      notEmpty: true,
    },
  },
});

export default ((
  Sequelize,
  stack,
): $ModelClass<'RegattaGallery'> => {
  const RegattaGallery = stack.define(
    'RegattaGallery',
    fields(Sequelize),
    {
      tableName: 'RegattaGalleries',
    },
  );

  RegattaGallery.associate = (models: $ModelClasses) => {
    models.RegattaGallery.belongsTo(
      models.Regatta,
      {
        as: 'Regatta',
        foreignKey: 'RegattaId',
      },
    );
  };

  RegattaGallery.addScope(
    'defaultScope',
    {
      order: [['createdAt', 'ASC']],
    },
  );

  return RegattaGallery;
});
